import { useMemo, useState } from 'react';
import { ChevronDown, Plus, Search } from 'lucide-react';
import {
  NEW_ANALYSIS_ACCOUNT_VALUE,
  filterAnalysisAccounts,
  isManualReportAnalysisProfileId,
} from '@/components/analysis/analysisAccountMode';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { cn } from '@/utils/cn';
import { advertisingAccountSelectLabel, type LinkedAccountRow } from '@/api/managerAccounts';

type SelectableAccount = LinkedAccountRow & { profileId: string };

function AccountOption({
  row,
  selected,
  onPick,
}: {
  row: SelectableAccount;
  selected: boolean;
  onPick: (profileId: string) => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      className={cn(
        'flex w-full rounded-md px-2.5 py-2 text-left text-body-sm text-foreground hover:bg-surface-muted',
        selected && 'bg-surface-muted'
      )}
      onClick={() => onPick(row.profileId)}
    >
      <span className="truncate">{advertisingAccountSelectLabel(row)}</span>
    </button>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="px-2.5 pb-1 pt-2 text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle">
      {children}
    </p>
  );
}

export function AnalysisAccountSelect({
  accounts,
  value,
  onChange,
}: {
  accounts: SelectableAccount[];
  value: string;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const isNew = value === NEW_ANALYSIS_ACCOUNT_VALUE;
  const selected = accounts.find((row) => row.profileId === value);
  const filtered = useMemo(
    () => filterAnalysisAccounts(accounts, query, advertisingAccountSelectLabel),
    [accounts, query]
  );
  const { connectedAccounts, manualAccounts } = useMemo(() => {
    const connected: SelectableAccount[] = [];
    const manual: SelectableAccount[] = [];
    for (const row of filtered) {
      if (isManualReportAnalysisProfileId(row.profileId)) manual.push(row);
      else connected.push(row);
    }
    return { connectedAccounts: connected, manualAccounts: manual };
  }, [filtered]);

  const triggerLabel = isNew
    ? 'Add new account'
    : selected
      ? advertisingAccountSelectLabel(selected)
      : 'Select an account';

  const pick = (next: string) => {
    onChange(next);
    setOpen(false);
    setQuery('');
  };

  const noMatches =
    filtered.length === 0 && accounts.length > 0 && query.trim().length > 0;

  return (
    <div>
      <label
        htmlFor="advertising-account"
        className="mb-1.5 block text-label-md font-semibold uppercase tracking-[0.08em] text-foreground-subtle"
      >
        Account
      </label>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery('');
        }}
      >
        <PopoverTrigger asChild>
          <button
            id="advertising-account"
            type="button"
            className="flex h-[42px] w-full items-center justify-between gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-left text-body-md text-foreground shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className={cn('truncate', !selected && !isNew && 'text-foreground-subtle')}>
              {triggerLabel}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-foreground-muted" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-1.5">
          <div className="relative mb-1.5">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-subtle" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search accounts..."
              className="h-9 w-full rounded-md border border-border bg-surface pl-8 pr-3 text-body-sm text-foreground outline-none placeholder:text-foreground-subtle focus:border-brand focus:ring-2 focus:ring-brand/30"
            />
          </div>
          <div className="max-h-56 overflow-y-auto" role="listbox">
            {noMatches ? (
              <p className="px-2 py-2 text-body-sm text-foreground-muted">No matching accounts</p>
            ) : null}
            {connectedAccounts.length > 0 ? (
              <>
                <SectionLabel>Connected accounts</SectionLabel>
                {connectedAccounts.map((row) => (
                  <AccountOption
                    key={row.profileId}
                    row={row}
                    selected={row.profileId === value}
                    onPick={pick}
                  />
                ))}
              </>
            ) : null}
            {manualAccounts.length > 0 ? (
              <>
                <SectionLabel>Manually added accounts</SectionLabel>
                {manualAccounts.map((row) => (
                  <AccountOption
                    key={row.profileId}
                    row={row}
                    selected={row.profileId === value}
                    onPick={pick}
                  />
                ))}
              </>
            ) : null}
            <button
              type="button"
              role="option"
              aria-selected={isNew}
              className={cn(
                'mt-0.5 flex w-full items-start gap-2 rounded-md px-2.5 py-2 text-left hover:bg-brand-subtle',
                isNew && 'bg-brand-subtle'
              )}
              onClick={() => pick(NEW_ANALYSIS_ACCOUNT_VALUE)}
            >
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-brand/30 bg-surface text-brand">
                <Plus className="h-3 w-3" aria-hidden />
              </span>
              <span className="min-w-0">
                <span className="block text-body-sm font-medium text-foreground">Add new account</span>
                <span className="mt-0.5 block text-body-sm text-foreground-muted">
                  Manually add an account and upload console reports
                </span>
              </span>
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
